/**
 * voice-v8 — Seite „Training“ IN der App. (21.10.2026, Martin: „Eigene Seite anlegen, damit ich auch Statistik habe.“)
 *
 * Ein Blatt über dem Chat wie „Morgenreport“, reloadfest über `#training`. Zwei Teile: die Woche (dieselben sieben Zeilen und
 * „Erledigt“-Knöpfe wie die Chat-Karte — `baueTrainingWoche` in karten/training.ts, dieselbe feste Meldung, kein freier Text) und die
 * Statistik aus GET /api/training/statistik (server/training-routen.ts, nur Konto 1; jedes andere Konto bekommt 403 und sieht
 * „nicht verfügbar“ — der Menüpunkt steht ohnehin nur bei Konto 1).
 *
 * 22.10.2026 (Martin: „komische Trainingsübersichtsseite … hat überhaupt keine Struktur … Was mache ich damit?“): zwei Karten in einer
 * schmalen Lesespalte. „Diese Woche“ trägt oben einen Wochenstreifen Mo–So (ein Punkt je Tag, heute umrandet) — auch ohne Plan, dann
 * leer — und im Leerzustand GENAU EINE Handlung: „Plan anlegen“ legt einen Vorschlag an Vespera ins Eingabefeld (Ereignis
 * `voice-v8:ins-feld`, app.ts) und sendet NICHTS. „Statistik“ zeigt leer stille Platzhalter-Balken statt Fließtext. Daten- und
 * Planlogik unverändert (dieselbe Antwort, dieselben Wochenzeilen und „Erledigt“-Knöpfe).
 *
 * ⚠️ Keine erfundenen Zahlen: was die Daten nicht hergeben (Quote ohne Plan, Gefühl ohne Angaben), steht als Satz, nie als 0.
 * ⚠️ Balken sind `<progress>` MIT sichtbarer Zahl daneben („3 von 4“) — nie nur Grafik. Alle Texte über textContent, Aussehen in
 *    style.css (.trs-*), keine Inline-Styles. Whoop wird hier nicht abgefragt.
 */
import { baueTrainingWoche, trainingArtWort } from "./karten/training.js";
import { clearPageHash, setPageHash } from "./page-routes.js";
import { buildLoadingHint, buildPageHeader } from "./sheet-ui.js";

export const SEITEN_SCHLUESSEL = "training";
export const SEITEN_ID = "training-sheet";
export const STATISTIK_PFAD = "/api/training/statistik";

export interface TrainingWoche {
  start: string;
  geplant: number;
  erledigt: number;
  min: number;
}
export interface TrainingStatistik {
  wochen: TrainingWoche[];
  geplant: number;
  erledigt: number;
  quote_pct: number | null;
  serie_tage: number;
  serie_wochen: number;
  dauer_je_art: Array<{ art: string; min: number; anzahl: number }>;
  gefuehl_mittel: number | null;
  eintraege: number;
  gesamt_min: number;
}
export interface TrainingDaten {
  woche: unknown;
  statistik: TrainingStatistik;
}

const zahl = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);

/** Form-Prüfung der Serverantwort; alles andere: null. Rein. */
export function leseTrainingDaten(roh: unknown): TrainingDaten | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as { ok?: unknown; woche?: unknown; statistik?: Partial<TrainingStatistik> | null };
  const s = r.statistik;
  if (r.ok !== true || !s || !Array.isArray(s.wochen) || !Array.isArray(s.dauer_je_art)) return null;
  const wochen = s.wochen.filter((w): w is TrainingWoche => !!w && typeof w.start === "string" && zahl(w.geplant) !== null && zahl(w.erledigt) !== null && zahl(w.min) !== null);
  const jeArt = s.dauer_je_art.filter((a) => !!a && typeof a.art === "string" && zahl(a.min) !== null && zahl(a.anzahl) !== null);
  return {
    woche: r.woche,
    statistik: {
      wochen,
      geplant: zahl(s.geplant) ?? 0,
      erledigt: zahl(s.erledigt) ?? 0,
      quote_pct: zahl(s.quote_pct),
      serie_tage: zahl(s.serie_tage) ?? 0,
      serie_wochen: zahl(s.serie_wochen) ?? 0,
      dauer_je_art: jeArt,
      gefuehl_mittel: zahl(s.gefuehl_mittel),
      eintraege: zahl(s.eintraege) ?? 0,
      gesamt_min: zahl(s.gesamt_min) ?? 0,
    },
  };
}

export async function holeTrainingDaten(): Promise<TrainingDaten | null> {
  try {
    const r = await fetch(STATISTIK_PFAD, { credentials: "same-origin", cache: "no-store" });
    if (!r.ok) return null;
    return leseTrainingDaten(await r.json());
  } catch {
    return null;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

const kurzDatum = (iso: string): string => {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[2]}.${m[1]}.` : iso;
};

export const minutenText = (min: number): string => {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r === 0 ? `${h} h` : `${h} h ${r} min`;
};

const mehrzahl = (n: number, eins: string, viele: string): string => `${n} ${n === 1 ? eins : viele}`;

function abschnitt(titel: string, id: string, ...inhalt: Node[]): HTMLElement {
  const s = el("section", "trs-abschnitt");
  s.setAttribute("aria-labelledby", id);
  const h = el("h3", "trs-titel", titel);
  h.id = id;
  s.append(h, ...inhalt);
  return s;
}

function leer(satz: string, zusatz: string): HTMLElement {
  const p = el("p", "trs-leer");
  p.append(el("strong", "trs-leer-satz", satz), document.createTextNode(` ${zusatz}`));
  return p;
}

function kennzahl(name: string, wert: string, hinweis?: string): HTMLElement {
  const d = el("div", "trs-kennzahl");
  d.append(el("dt", "trs-kennzahl-name", name), el("dd", "trs-kennzahl-wert", wert));
  if (hinweis) d.appendChild(el("dd", "trs-kennzahl-hinweis", hinweis));
  return d;
}

function wochenReihe(w: TrainingWoche, laufend: boolean): HTMLElement {
  const li = el("li", "trs-woche-zeile");
  const name = `Woche ab ${kurzDatum(w.start)}`;
  li.appendChild(el("span", "trs-woche-name", `${kurzDatum(w.start)}${laufend ? " (jetzt)" : ""}`));
  if (w.geplant > 0) {
    const p = el("progress", "trs-balken");
    p.max = w.geplant;
    p.value = Math.min(w.erledigt, w.geplant);
    p.setAttribute("aria-label", `${name}: ${w.erledigt} von ${w.geplant} erledigt`);
    p.textContent = `${w.erledigt} von ${w.geplant}`;
    li.append(p, el("span", "trs-woche-zahl", `${w.erledigt} von ${w.geplant}`));
  } else {
    li.append(el("span", "trs-balken-leer", "nichts geplant"), el("span", "trs-woche-zahl", w.min > 0 ? minutenText(w.min) : "–"));
  }
  return li;
}

/** Die Statistik als Abschnitte. Leer (nichts geplant, nichts eingetragen): ein ruhiger Satz statt Nullen. */
export function baueStatistik(s: TrainingStatistik): HTMLElement[] {
  if (s.geplant === 0 && s.eintraege === 0) {
    return [karte(abschnitt("Statistik", "trs-t-stat", platzhalterBalken(s.wochen), leer("Noch keine Einträge.", "Sobald du ein Training als erledigt meldest, wächst hier je Woche ein Balken.")))];
  }
  const kz = el("dl", "trs-kennzahlen");
  kz.append(
    kennzahl("Quote", s.quote_pct === null ? "–" : `${s.quote_pct} %`, s.quote_pct === null ? "nichts geplant" : `${s.erledigt} von ${s.geplant} Blöcken`),
    kennzahl("Serie", mehrzahl(s.serie_tage, "Tag", "Tage"), s.serie_wochen > 0 ? `${mehrzahl(s.serie_wochen, "Woche", "Wochen")} in Folge` : "in Folge"),
    kennzahl("Zusammen", s.gesamt_min > 0 ? minutenText(s.gesamt_min) : "–", mehrzahl(s.eintraege, "Eintrag", "Einträge")),
  );
  if (s.gefuehl_mittel !== null) kz.appendChild(kennzahl("Gefühl im Mittel", `${String(s.gefuehl_mittel).replace(".", ",")} von 5`, "1 schlecht, 5 sehr gut"));

  const liste = el("ol", "trs-wochen");
  s.wochen.forEach((w, i) => liste.appendChild(wochenReihe(w, i === s.wochen.length - 1)));
  const teile: HTMLElement[] = [
    karte(abschnitt("Statistik", "trs-t-stat", kz)),
    karte(abschnitt(`Letzte ${s.wochen.length} Wochen`, "trs-t-wochen", el("p", "trs-fein", "Erledigte von geplanten Blöcken je Woche, Beginn am Montag."), liste)),
  ];
  if (s.dauer_je_art.length > 0) {
    const ul = el("ul", "trs-arten");
    for (const a of s.dauer_je_art) {
      const li = el("li", "trs-art");
      li.append(el("span", "trs-art-name", trainingArtWort(a.art) || "Training"), el("span", "trs-art-wert", `${minutenText(a.min)} · ${a.anzahl} Mal`));
      ul.appendChild(li);
    }
    teile.push(karte(abschnitt("Dauer je Art", "trs-t-arten", ul)));
  }
  return teile;
}

/** Der Vorschlag, den „Plan anlegen“ ins Eingabefeld legt — Martin ergänzt ihn und schickt selbst ab. */
export const PLAN_VORSCHLAG = "Leg mir einen Trainings-Wochenplan an: ";

const TAG_KURZ = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
const TAG_LANG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"] as const;
const MONATE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"] as const;
const STAND_WORT: Record<string, string> = { erledigt: "erledigt", teilweise: "teilweise erledigt", offen: "offen", frei: "frei", leer: "kein Plan" };

export interface StreifenTag {
  /** 0 = Montag … 6 = Sonntag */
  i: number;
  datum: Date;
  stand: "erledigt" | "teilweise" | "offen" | "frei" | "leer";
  heute: boolean;
}

const isoTag = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Die sieben Tage der laufenden Woche (Mo–So) für den Streifen. Mit Plan nimmt jeder Tag seinen Stand aus der Serverantwort (dasselbe
 * Feld `stand` wie die Wochenzeilen); ohne Plan stehen die Tage leer. Heute: aus der Antwort, sonst nach der Uhr dieses Geräts. Rein.
 */
export function streifenTage(woche: unknown, jetzt: Date = new Date()): StreifenTag[] {
  const r = (woche && typeof woche === "object" ? woche : {}) as { plan?: unknown; woche?: unknown };
  const tage = r.plan && Array.isArray(r.woche) ? (r.woche as Array<{ wochentag?: unknown; datum?: unknown; stand?: unknown; heute?: unknown }>) : [];
  const montag = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate() - ((jetzt.getDay() + 6) % 7));
  const heuteIso = isoTag(jetzt);
  const ausDaten = tage.length === 7 && tage.every((t) => typeof t?.datum === "string" && /^\d{4}-\d{2}-\d{2}$/.test(t.datum));
  return TAG_KURZ.map((_, i) => {
    const t = ausDaten ? (tage[i] ?? null) : null;
    const datum = t ? new Date(`${t.datum as string}T12:00:00`) : new Date(montag.getFullYear(), montag.getMonth(), montag.getDate() + i);
    const roh = t?.stand;
    const stand: StreifenTag["stand"] = !t ? "leer" : roh === "erledigt" || roh === "teilweise" || roh === "offen" ? roh : "frei";
    return { i, datum, stand, heute: t ? t.heute === true : isoTag(datum) === heuteIso };
  });
}

/** „20. bis 26. Oktober“ bzw. „27. Oktober bis 2. November“. Rein. */
export function wochenSpanne(tage: StreifenTag[]): string {
  const a = tage[0]?.datum;
  const b = tage[tage.length - 1]?.datum;
  if (!a || !b) return "";
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()}. bis ${b.getDate()}. ${MONATE[b.getMonth()]}`
    : `${a.getDate()}. ${MONATE[a.getMonth()]} bis ${b.getDate()}. ${MONATE[b.getMonth()]}`;
}

/** Der Wochenstreifen: je Tag Kürzel, Punkt (Stand) und Datum; heute umrandet. Keine Knöpfe — eine Übersicht, vorgelesen je Tag. */
function wochenStreifen(tage: StreifenTag[]): HTMLElement {
  const ol = el("ol", "trs-streifen");
  ol.setAttribute("aria-label", "Die Woche im Überblick");
  for (const t of tage) {
    const li = el("li", `trs-streifen-tag trs-streifen-tag--${t.stand}${t.heute ? " trs-streifen-tag--heute" : ""}`);
    li.setAttribute("aria-label", `${TAG_LANG[t.i]} ${t.datum.getDate()}.${t.datum.getMonth() + 1}.${t.heute ? ", heute" : ""}: ${STAND_WORT[t.stand]}`);
    const kurz = el("span", "trs-streifen-kurz", TAG_KURZ[t.i]);
    const punkt = el("span", "trs-streifen-punkt");
    const zahl = el("span", "trs-streifen-zahl", String(t.datum.getDate()));
    for (const k of [kurz, punkt, zahl]) k.setAttribute("aria-hidden", "true");
    li.append(kurz, punkt, zahl);
    ol.appendChild(li);
  }
  return ol;
}

/** Leere Statistik: je Woche eine stille Spur (nur Optik, für Vorleser verborgen) mit erstem und letztem Datum darunter. */
function platzhalterBalken(wochen: TrainingWoche[]): HTMLElement {
  const n = wochen.length > 0 ? wochen.length : 8;
  const box = el("div", "trs-platzhalter");
  box.setAttribute("aria-hidden", "true");
  const reihe = el("div", "trs-platzhalter-reihe");
  for (let i = 0; i < n; i++) reihe.appendChild(el("span", "trs-platzhalter-spur"));
  box.appendChild(reihe);
  if (wochen.length > 1) {
    const achse = el("div", "trs-platzhalter-achse");
    achse.append(el("span", "", kurzDatum(wochen[0]?.start ?? "")), el("span", "", "diese Woche"));
    box.appendChild(achse);
  }
  return box;
}

function karte(...inhalt: Node[]): HTMLElement {
  const k = el("div", "trs-karte");
  k.append(...inhalt);
  return k;
}

/** „Plan anlegen“: der Vorschlag ins Eingabefeld (app.ts hört auf `voice-v8:ins-feld`) — nichts wird gesendet. */
function planAnlegenKnopf(): HTMLElement {
  const b = el("button", "btn btn--primary trs-aktion", "Plan anlegen");
  b.type = "button";
  b.addEventListener("click", () => window.dispatchEvent(new CustomEvent("voice-v8:ins-feld", { detail: { text: PLAN_VORSCHLAG } })));
  return b;
}

export function zeichneTrainingSeite(body: HTMLElement, d: TrainingDaten, jetzt: Date = new Date()): void {
  const tage = streifenTage(d.woche, jetzt);
  const woche = baueTrainingWoche(d.woche);
  const kopf = el("div", "trs-karte-kopf");
  const titel = el("h3", "trs-titel", "Diese Woche");
  titel.id = "trs-t-woche";
  kopf.append(titel, el("span", "trs-spanne", wochenSpanne(tage)));
  const wochenKarte = el("section", "trs-karte trs-karte--woche");
  wochenKarte.setAttribute("aria-labelledby", "trs-t-woche");
  wochenKarte.append(kopf, wochenStreifen(tage));
  if (woche) {
    const liste = el("div", "trs-wochenkarte tool-training");
    liste.appendChild(woche);
    wochenKarte.appendChild(liste);
  } else {
    const leerTeil = el("div", "trs-leer-teil");
    leerTeil.append(
      el("p", "trs-leer-satz", "Noch kein Plan."),
      el("p", "trs-fein", "Vespera schlägt dir einen Wochenplan vor; er gilt erst mit deinem Ja. Der Knopf legt die Bitte ins Eingabefeld – abschicken tust du selbst."),
      planAnlegenKnopf(),
    );
    wochenKarte.appendChild(leerTeil);
  }
  const arzt = el("p", "trs-hinweis", "Kein ärztlicher Rat. Bei Schmerz, Schwindel oder Brustdruck: Training aus und ärztlich abklären lassen.");
  body.replaceChildren(wochenKarte, ...baueStatistik(d.statistik), arzt);
}

export function closeTrainingSeite(): void {
  document.getElementById(SEITEN_ID)?.remove();
  clearPageHash(SEITEN_SCHLUESSEL);
}

export async function openTrainingSeite(): Promise<void> {
  if (document.getElementById(SEITEN_ID)) return;
  window.dispatchEvent(new CustomEvent("voice-v8:page-open"));
  const sheet = el("div", "sheet page-sheet trs-sheet");
  sheet.id = SEITEN_ID;
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Training");
  const card = el("div", "sheet-card");
  const body = el("div", "trs-body page-body");
  card.append(buildPageHeader("Training", closeTrainingSeite), body);
  sheet.appendChild(card);
  document.body.appendChild(sheet);
  setPageHash(SEITEN_SCHLUESSEL);
  body.replaceChildren(buildLoadingHint("Lade Training …"));
  const d = await holeTrainingDaten();
  if (!sheet.isConnected) return;
  if (!d) {
    body.replaceChildren(leer("Das Training ließ sich gerade nicht laden.", "Bitte später noch einmal öffnen."));
    return;
  }
  zeichneTrainingSeite(body, d);
}
